<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { groupBy } from 'lodash-es'
import type { Instance } from 'pspdfkit'

interface Props {
  instance: Instance
}

const props = defineProps<Props>()

const { t } = useI18n()

const searchState = computed(() => props.instance.searchState)

const normalizedResults = computed(() => {
  if (!searchState.value?.results) return []

  const normalizedResults = searchState.value?.results.toJS()

  const enhancedResults = normalizedResults.map((result: any, index: number) => {
    return {
      ...result,
      totalResultIndex: index,
    }
  })

  return enhancedResults
})

const resultsGroupsByPage = computed(() =>
  groupBy(normalizedResults.value, (c) => {
    return c.pageIndex
  }),
)

const focusedResultIndexText = computed(() => {
  if (!searchState.value?.results) return ''

  const focusedResultIndex = searchState.value.focusedResultIndex
  const resultCount = searchState.value.results.size

  return `${focusedResultIndex !== -1 ? focusedResultIndex + 1 : 0} ${t(
    'canvasUI.components.fileViewer.sidebars.searchResults.of',
  )} ${resultCount}`
})

const jumpToHighlightedText = (searchResult: any, resultIndex: number) => {
  props.instance.setSearchState(searchState.value.set('focusedResultIndex', resultIndex))

  props.instance.jumpToRect(
    searchResult.pageIndex,
    searchResult.isAnnotation
      ? searchResult.annotationRect
      : window.PSPDFKit.Geometry.Rect.union(
          searchState.value?.results?.find?.((x) => x.pageIndex === searchResult.pageIndex)?.rectsOnPage,
        ),
  )
}
</script>

<template>
  <div class="c-file-viewer-search-results-sidebar">
    <div v-if="searchState && searchState.results" class="c-file-viewer-search-results-sidebar-header">
      <div>{{ t('canvasUI.components.fileViewer.sidebars.searchResults.header') }}</div>
      <div>{{ focusedResultIndexText }}</div>
    </div>
    <template v-if="searchState && searchState.results">
      <div v-for="(results, focusedResultIndex) in resultsGroupsByPage" :key="focusedResultIndex">
        <div class="c-file-viewer-search-results-sidebar__page">
          <div>{{ t('canvasUI.components.fileViewer.sidebars.searchResults.page') }}</div>
          <div>{{ results[0].pageIndex + 1 }}</div>
        </div>
        <div v-for="(result, index) in results" :key="index" class="c-file-viewer-search-results-sidebar__page-results">
          <span>
            {{ result.previewText.substring(0, result.locationInPreview) }}
          </span>
          <mark :key="1" :style="{ cursor: 'pointer' }" @click="jumpToHighlightedText(result, result.totalResultIndex)">
            {{
              result.previewText.substring(result.locationInPreview, result.locationInPreview + searchState.term.length)
            }}
          </mark>
          <span>
            {{ result.previewText.substring(result.locationInPreview + searchState.term.length) }}
          </span>
          <hr class="c-file-viewer-search-results-sidebar__page-results-divider" />
        </div>
      </div>
    </template>
  </div>
</template>
